// src/pages/DashboardPage.jsx
import React, { useEffect } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../hooks/useAuth';
import { Link } from 'react-router-dom';
import { 
    UserGroupIcon, 
    DocumentTextIcon, 
    ChartBarIcon, 
    CogIcon,
    CheckCircleIcon,
    BuildingOfficeIcon 
} from '@heroicons/react/24/outline';

const DashboardPage = () => {
    const { user, isProfessional, isSupervisor, isManager, getUserRole } = useAuth();

    // DEBUG: Ver qué datos llegan del usuario
    //useEffect(() => {
        //console.log('🔍 DATOS DEL USUARIO EN DASHBOARD:', user);
        //console.log('🎭 ROLES DETECTADOS - Profesional:', isProfessional, 'Supervisor:', isSupervisor, 'Gerente:', isManager);
        //console.log('🎭 getUserRole value:', getUserRole);
        //if (user) {
        //console.log('📋 Campos disponibles:', Object.keys(user));
        //}
    //}, [user, isProfessional, isSupervisor, isManager, getUserRole]);

    const { 
		canManageUsers, 
		canManageCompanies, 
		canManageActas, 
		canViewMetricas 
	} = usePermissions();

    // ✅ CORRECCIÓN: Stats disponibles para cada tipo de usuario
    const getStatsForUser = () => {
        const baseStats = [
            {
                name: 'Mi Perfil',
                description: 'Gestiona tu información personal',
                icon: UserGroupIcon,
                href: '/my-profile',
                color: 'bg-blue-500',
                available: true,
            },
            {
                name: 'Gestión de Actas',
                description: isProfessional ? 'Crear y ver actas' : 'Crear y gestionar actas de servicio',
                icon: DocumentTextIcon,
                href: '/actas',
                color: 'bg-indigo-500',
                available: true,
            }
        ];

        // Solo supervisores/gerentes ven estas opciones
        if (canManageUsers) {
            baseStats.push(
                {
                    name: 'Gestión de Usuarios',
                    description: 'Administra usuarios y permisos',
                    icon: UserGroupIcon,
                    href: '/users',
                    color: 'bg-green-500',
                    available: true,
                },
                {
                    name: 'Gestión de Empresas',
                    description: 'Administra empresas del sistema',
                    icon: BuildingOfficeIcon,
                    href: '/companies',
                    color: 'bg-indigo-500',
                    available: true,
                }
            );
        }

        // Solo supervisores/gerentes ven métricas
        if (canViewMetricas) {
            baseStats.push({
                name: 'Métricas y Reportes',
                description: 'Ver reportes y estadísticas',
                icon: ChartBarIcon,
                href: '/metricas',
                color: 'bg-purple-500',
                available: true,
            },
            {
                name: 'Reportes Avanzados',
                description: 'Reportes detallados del sistema',
                icon: ChartBarIcon,
                href: '/reports',
                color: 'bg-purple-500',
                available: false,
            });
        }

        return baseStats;
    };

    const stats = getStatsForUser();

    const getRoleDisplay = () => {
        const roles = {
            professional: { 
                name: 'Profesional', 
                description: 'Crear y gestionar actas de servicio' 
            },
            accountant: { // Para compatibilidad
                name: 'Profesional',
                description: 'Crear y gestionar actas de servicio'
            },
            supervisor: { 
                name: 'Supervisor', 
                description: 'Gestión de usuarios y contenidos' 
            },
            manager: { 
                name: 'Gerente', 
                description: 'Acceso completo al sistema' 
            },
            user: {
                name: 'Usuario',
                description: 'Usuario del sistema'
            }
        };
        
        // ✅ CORRECCIÓN: getUserRole es un string, no una función
        const userRole = getUserRole || user?.role || user?.profile?.role || 'user';
        return roles[userRole] || roles.user;
    };

    const roleInfo = getRoleDisplay();

    // Función para obtener el nombre para mostrar
    const getDisplayName = () => {
        if (user?.first_name && user?.last_name) {
            return `${user.first_name} ${user.last_name}`;
        }
        if (user?.full_name) {
            return user.full_name;
        }
        if (user?.profile?.first_name && user?.profile?.last_name) {
            return `${user.profile.first_name} ${user.profile.last_name}`;
        }
        return user?.username || 'Usuario';
    };

    // Función para obtener el valor por hora
    const getValorHora = () => {
        return user?.valor_hora || user?.profile?.valor_hora || user?.user?.valor_hora || 'No asignado';
    };

    // Función para obtener el teléfono
    const getPhoneNumber = () => {
        return user?.phone_number || user?.profile?.phone_number || user?.user?.phone_number || 'No registrado';
    };

    // Función para obtener el email
    const getEmail = () => {
        return user?.email || user?.profile?.email || user?.user?.email || 'No disponible';
    };

    // Función para obtener el rol
    const getRole = () => {
        // ✅ CORRECCIÓN: getUserRole es un string, no una función
        return getUserRole || user?.role || user?.profile?.role || 'No asignado';
    };

    // ✅ MEJORADO: Función para mostrar información específica del profesional
    const renderProfessionalInfo = () => {
        if (!isProfessional) return null;

        const valorHora = getValorHora();
        
        return (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <DocumentTextIcon className="h-6 w-6 text-blue-500 mr-3" />
                    <div>
                        <h3 className="font-semibold text-blue-800">Acceso de Profesional</h3>
                        <p className="text-blue-600 text-sm">
                            Puedes crear actas y visualizar solo tus documentos. 
                            {valorHora !== 'No asignado' && ` Valor hora: $${valorHora}`}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    // ✅ NUEVO: Función para mostrar información de supervisor/gerente
    const renderSupervisorInfo = () => {
        if (!isSupervisor && !isManager) return null;

        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                    <UserGroupIcon className="h-6 w-6 text-green-500 mr-3" />
                    <div>
                        <h3 className="font-semibold text-green-800">
                            Acceso de {isManager ? 'Gerente' : 'Supervisor'}
                        </h3>
                        <p className="text-green-600 text-sm">
                            Tienes acceso completo al sistema incluyendo gestión de usuarios, empresas y métricas.
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Welcome Section */}
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                            ¡Bienvenido, {getDisplayName()}!
                        </h1>
                            <p className="mt-2 text-gray-600 text-sm sm:text-base">
                            {roleInfo.description}
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 bg-blue-50 px-4 py-2 rounded-lg self-start sm:self-auto">
                        <CheckCircleIcon className="h-5 w-5 text-blue-500" />
                        <span className="text-blue-700 font-medium capitalize text-sm sm:text-base">
                            {roleInfo.name}
                        </span>
                    </div>
                </div>
            </div>

            {/* ✅ MEJORADO: Información específica por rol */}
            {renderProfessionalInfo()}
            {renderSupervisorInfo()}

            {/* Stats Grid - Responsive */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 mb-8">
                {stats.map((item) => (
                <Link
                    key={item.name}
                    to={item.available ? item.href : '#'}
                    className={`block p-4 sm:p-6 rounded-lg shadow-sm border transition-all duration-200 ${
                    item.available 
                        ? 'bg-white border-gray-200 hover:shadow-md hover:border-blue-300 cursor-pointer' 
                        : 'bg-gray-50 border-gray-100 cursor-not-allowed'
                    }`}
                    onClick={!item.available ? (e) => e.preventDefault() : undefined}
                >
                    <div className="flex items-center">
                        <div className={`flex-shrink-0 flex items-center justify-center h-10 w-10 sm:h-12 sm:w-12 rounded-md ${item.color} text-white`}>
                            <item.icon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </div>
                        <div className="ml-3 sm:ml-4 flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                                <h3 className="text-base sm:text-lg font-medium text-gray-900 truncate">
                                    {item.name}
                                </h3>
                                {!item.available && (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 whitespace-nowrap ml-2">
                                    Próximamente
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-xs sm:text-sm text-gray-500 line-clamp-2">
                                {item.description}
                            </p>
                        </div>
                    </div>
                </Link>
                ))}
            </div>

            {/* Quick Actions - Responsive */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Acciones Rápidas</h2>
                <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                <Link
                    to="/my-profile"
                    className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                    <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 text-blue-500 mr-2" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base">Mi Perfil</span>
                    </div>
                </Link>
                
                <Link
                    to="/actas"
                    className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                >
                    <div className="flex items-center">
                        <DocumentTextIcon className="h-5 w-5 text-indigo-500 mr-2" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base">
                            {isProfessional ? 'Actas' : 'Gestionar Actas'}
                        </span>
                    </div>
                </Link>
                
                {canManageUsers && (
                    <Link
                        to="/users"
                        className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-colors"
                    >
                    <div className="flex items-center">
                        <UserGroupIcon className="h-5 w-5 text-green-500 mr-2" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base">Gestionar Usuarios</span>
                    </div>
                    </Link>
                )}
                
                {canManageCompanies && (
                    <Link
                        to="/companies"
                        className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    >
                        <div className="flex items-center">
                            <BuildingOfficeIcon className="h-5 w-5 text-indigo-500 mr-2" />
                            <span className="font-medium text-gray-900 text-sm sm:text-base">Gestionar Empresas</span>
                        </div>
                    </Link>
                )}

                {canViewMetricas && (
                    <Link
                        to="/metricas"
                        className="p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors"
                    >
                    <div className="flex items-center">
                        <ChartBarIcon className="h-5 w-5 text-purple-500 mr-2" />
                        <span className="font-medium text-gray-900 text-sm sm:text-base">Ver Métricas</span>
                    </div>
                    </Link>
                )}
                </div>
            </div>

            {/* Información de Sesión - Responsive */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Estado del Sistema</h3>
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-sm sm:text-base">Autenticación</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Activa
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-sm sm:text-base">Base de Datos</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Conectada
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600 text-sm sm:text-base">API Backend</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Online
                            </span>
                        </div>
                        {isProfessional && (
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600 text-sm sm:text-base">Modo Profesional</span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    Activo
                                </span>
                            </div>
                        )}
                        {(isSupervisor || isManager) && (
                            <div className="flex items-center justify-between">
                                <span className="text-gray-600 text-sm sm:text-base">
                                    Modo {isManager ? 'Gerente' : 'Supervisor'}
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Activo
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Información de Sesión</h3>
                    <div className="space-y-2 text-sm sm:text-base">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Usuario:</span>
                            <span className="font-medium text-right">{user?.username || 'No disponible'}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Email:</span>
                            <span className="font-medium text-right">{getEmail()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Nombre:</span>
                            <span className="font-medium text-right">{getDisplayName()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Teléfono:</span>
                            <span className="font-medium text-right">{getPhoneNumber()}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Rol:</span>
                            <span className="font-medium capitalize text-right">{getRole()}</span>
                        </div>
                        
                        <div className="flex justify-between">
                            <span className="text-gray-600">Sesión activa desde:</span>
                            <span className="font-medium text-right text-xs sm:text-sm">
                                {new Date().toLocaleTimeString()}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardPage;
